import {
  Assignment,
  Chat,
  ChevronRight,
  Email,
  ExpandMore,
  Headset,
  Phone,
  Security,
  Support,
  SupportAgent,
  VideoCall,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Fade,
  Grid,
  Grow,
  Paper,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';

// Animasyonlar
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;


const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(25, 118, 210, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(25, 118, 210, 0.6);
  }
`;

// Styled Components
const AnimatedCard = styled(Card)(({ theme, delay = 0 }) => ({
  height: '100%',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  transform: 'translateY(0)',
  animation: `${fadeInUp} 0.8s ease-out ${delay}s both`,
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s',
  },
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    '&::before': {
      left: '100%',
    },
  },
}));

const GlowingIcon = styled(Box)(({ color }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
  boxShadow: `0 8px 32px ${color}40`,
  position: 'relative',
  transition: 'all 0.3s ease',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    right: '-2px',
    bottom: '-2px',
    borderRadius: '50%',
    background: `linear-gradient(45deg, ${color}, transparent, ${color})`,
    zIndex: -1,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    animation: `${glow} 2s infinite`,
    '&::after': {
      opacity: 1,
    },
  },
}));

const ActionButton = styled(Button)(({ bgcolor, hovercolor }) => ({
  background: `linear-gradient(135deg, ${bgcolor} 0%, ${bgcolor}dd 100%)`,
  color: 'white',
  fontWeight: 600,
  borderRadius: 25,
  padding: '10px 20px',
  textTransform: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)`,
    transition: 'left 0.5s',
  },
  '&:hover': {
    background: `linear-gradient(135deg, ${hovercolor} 0%, ${hovercolor}dd 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${bgcolor}40`,
    '&::before': {
      left: '100%',
    },
  },
}));

const FloatingIcon = styled(Box)(() => ({
  animation: `${float} 3s ease-in-out infinite`,
}));

const PulseButton = styled(Button)(() => ({
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    animation: 'none',
  },
}));

const TeknikDestek = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'normal'
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Animation trigger logic can be added here
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('[id]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const supportChannels = [
    {
      title: '7/24 Telefon Desteği',
      description: 'Acil durumlarda anında destek alın. Teknik uzmanlarımız 7 gün 24 saat hizmetinizdedir.',
      icon: <Phone sx={{ fontSize: 40 }} />,
      color: '#1976d2',
      contact: '0850 255 19 06',
      availability: '7/24 Hizmet',
      features: ['Anında çözüm', 'Uzman destek', 'Tüm ürünler'],
    },
    {
      title: 'E-posta Desteği',
      description: 'Detaylı sorularınız için e-posta gönderin. En geç 24 saat içinde yanıtlıyoruz.',
      icon: <Email sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      contact: 'teknikdestek@ronesis.com.tr',
      availability: '24 saat içinde yanıt',
      features: ['Detaylı çözüm', 'Döküman paylaşımı', 'Takip numarası'],
    },
    {
      title: 'Canlı Sohbet',
      description: 'Hızlı sorularınız için canlı destek kullanın. Anında yanıt alın.',
      icon: <Chat sx={{ fontSize: 40 }} />,
      color: '#ed6c02',
      contact: 'Online Sohbet',
      availability: 'Hafta içi 09:00-17:45',
      features: ['Anında yanıt', 'Dosya paylaşımı', 'Ekran paylaşımı'],
    },
    {
      title: 'Video Konferans',
      description: 'Karmaşık konular için video görüşme yapın. Ekran paylaşımı ile detaylı destek.',
      icon: <VideoCall sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      contact: 'Randevu Sistemi',
      availability: 'Önceden randevu',
      features: ['Ekran paylaşımı', 'Canlı demo', 'Eğitim desteği'],
    },
  ];

  const supportCategories = [
    {
      category: 'E-İmza Desteği',
      icon: <Security sx={{ fontSize: 30 }} />,
      color: '#d32f2f',
      topics: [
        'E-imza sertifikası kurulumu',
        'İmzalama sorunları',
        'Tarayıcı uyumluluğu',
        'Mobil cihaz desteği',
        'Sertifika yenileme',
      ],
    },
    {
      category: 'KEP Desteği',
      icon: <Assignment sx={{ fontSize: 30 }} />,
      color: '#2e7d32',
      topics: [
        'KEP hesabı açma',
        'E-posta yapılandırması',
        'Tebligat sorunları',
        'Arşiv yönetimi',
        'Entegrasyon desteği',
      ],
    },
    {
      category: 'EYP Desteği',
      icon: <SupportAgent sx={{ fontSize: 30 }} />,
      color: '#1976d2',
      topics: [
        'EYP sistemi kurulumu',
        'Kullanıcı yönetimi',
        'Workflow yapılandırması',
        'Raporlama sorunları',
        'Güvenlik ayarları',
      ],
    },
    {
      category: 'Genel Destek',
      icon: <Support sx={{ fontSize: 30 }} />,
      color: '#ed6c02',
      topics: [
        'Hesap yönetimi',
        'Faturalama sorunları',
        'Şifre sıfırlama',
        'Kullanıcı eğitimi',
        'Sistem gereksinimleri',
      ],
    },
  ];

  const faqItems = [
    {
      question: 'Teknik destek hizmetiniz ücretli mi?',
      answer: 'Temel teknik destek hizmetlerimiz ücretsizdir. Özel eğitim, özelleştirme ve yerinde destek hizmetleri için ayrı ücretlendirme yapılmaktadır.',
    },
    {
      question: 'Ne kadar sürede yanıt alırım?',
      answer: 'Telefon desteği anında, e-posta desteği 24 saat içinde, canlı sohbet çalışma saatleri içinde anında yanıtlanır.',
    },
    {
      question: 'Hangi saatlerde destek alabiliyorum?',
      answer: 'Telefon desteği 7/24, e-posta desteği 7/24, canlı sohbet ve video konferans hafta içi 09:00-17:45 saatleri arasında hizmet vermektedir.',
    },
    {
      question: 'Teknik destek için özel yazılım kurulum gerekiyor mu?',
      answer: 'Video konferans için browser desteği yeterlidir. Diğer destek kanalları için ek yazılım gerekmiyor.',
    },
    {
      question: 'Eski sürümler için destek veriliyor mu?',
      answer: 'Son 2 ana sürüm için tam destek, daha eski sürümler için sınırlı destek sağlanmaktadır.',
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Form submission logic here
    console.log('Form submitted:', formData);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        id="hero"
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #1976d2 0%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" sx={{
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            <Grid item xs={12} md={6} sx={{ flex: 1 }}>
              <Fade in timeout={1000}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}>
                  <Typography
                    variant="h1"
                    component="h1"
                    gutterBottom
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 700,
                      lineHeight: 1.2,
                      mb: 3,
                    }}
                  >
                    Teknik Destek
                    <Box component="span" sx={{ color: '#64b5f6', display: 'block' }}>
                      Uzman Yardım
                    </Box>
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 4,
                      opacity: 0.9,
                      fontSize: { xs: '1.1rem', md: '1.3rem' },
                      lineHeight: 1.6,
                    }}
                  >
                    7/24 teknik destek hizmetimizle yanınızdayız. Uzman ekibimiz
                    tüm sorularınızı yanıtlamaya ve sorunlarınızı çözmeye hazır.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <PulseButton
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: '#ff6b35',
                        '&:hover': { backgroundColor: '#e55a2e' },
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                      }}
                    >
                      Hemen Destek Al
                    </PulseButton>
                    {/* <Button
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          borderColor: 'white',
                        },
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                      }}
                    >
                      SSS'i İncele
                    </Button> */}
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Fade in timeout={1500}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <FloatingIcon>
                    <Box
                      sx={{
                        p: 4,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.2)',
                      }}
                    >
                      <Headset sx={{ fontSize: 80, color: 'white' }} />
                    </Box>
                  </FloatingIcon>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Support Channels Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f8fafe 0%, #e8f4fd 50%, #f8fafe 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 20%, rgba(25, 118, 210, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(25, 118, 210, 0.05) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 }, position: 'relative' }} id="channels">
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 3,
                  p: 2,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #1976d215, #1976d208)',
                  border: '1px solid #1976d220',
                }}
              >
                <Headset sx={{ fontSize: 40, color: '#1976d2' }} />
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    fontSize: { xs: '2rem', md: '2.5rem' },
                    fontWeight: 700,
                    color: '#1976d2',
                    mb: 0,
                  }}
                >
                  Destek Kanalları
                </Typography>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '650px',
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                Size en uygun destek kanalını seçin ve anında yardım alın
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mt: 3,
                }}
              >
                <Chip
                  label="7/24 Hizmet"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="Uzman Ekip"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="Hızlı Çözüm"
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
          </Fade>

          <Grid
            container
            spacing={3}
            sx={{
              justifyContent: 'center',
              alignItems: 'stretch',
              mt: 4,
            }}
          >
            {/* Tüm kartlar için sabit boyutlar ve mapping */}
            {supportChannels.map((channel, index) => (
              <Grid
                item
                xs={12}
                md={6}
                lg={6}
                key={index}
                sx={{
                  width: { xs: '100%', md: '48%' },
                }}
              >
                <Grow in timeout={1000 + index * 200}>
                  <AnimatedCard
                    delay={index * 0.2}
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      height: 480, // Daha yüksek sabit yükseklik
                      display: 'flex',
                      flexDirection: 'column',
                      background: `
                        linear-gradient(135deg,
                          ${channel.color}08 0%,
                          ${channel.color}15 50%,
                          ${channel.color}08 100%
                        )
                      `,
                      border: `2px solid ${channel.color}20`,
                      borderRadius: 4,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${channel.color}, ${channel.color}80, ${channel.color})`,
                        borderRadius: '16px 16px 0 0',
                      },
                      '&:hover': {
                        border: `2px solid ${channel.color}60`,
                        background: `
                          linear-gradient(135deg,
                            ${channel.color}15 0%,
                            ${channel.color}25 50%,
                            ${channel.color}15 100%
                          )
                        `,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        p: 2,
                        '&:last-child': {
                          paddingBottom: 2,
                        },
                      }}
                    >
                      {/* İkon alanı - sabit yükseklik */}
                      <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <GlowingIcon color={channel.color}>
                          {channel.icon}
                        </GlowingIcon>
                      </Box>

                      {/* Başlık alanı - sabit yükseklik */}
                      <Box sx={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: channel.color,
                            fontSize: '1.3rem',
                            textAlign: 'center',
                          }}
                        >
                          {channel.title}
                        </Typography>
                      </Box>

                      {/* Açıklama alanı - sabit yükseklik */}
                      <Box sx={{ height: 80, display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.5,
                            fontSize: '0.9rem',
                            textAlign: 'center',
                          }}
                        >
                          {channel.description}
                        </Typography>
                      </Box>

                      {/* İletişim kutusu - sabit yükseklik */}
                      <Box
                        sx={{
                          height: 70,
                          background: `linear-gradient(135deg, ${channel.color}15, ${channel.color}10)`,
                          borderRadius: 3,
                          p: 2,
                          mb: 2,
                          border: `1px solid ${channel.color}20`,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: channel.color,
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            mb: 0.5,
                          }}
                        >
                          {channel.contact}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontSize: '0.75rem',
                          }}
                        >
                          {channel.availability}
                        </Typography>
                      </Box>

                      {/* Özellik chip'leri - sabit yükseklik */}
                      <Box
                        sx={{
                          height: 60,
                          display: 'flex',
                          gap: 0.5,
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        {channel.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              backgroundColor: `${channel.color}15`,
                              color: channel.color,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              height: 24,
                              '&:hover': {
                                backgroundColor: `${channel.color}25`,
                                transform: 'scale(1.05)',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          />
                        ))}
                      </Box>

                      {/* Buton alanı - sabit yükseklik */}
                      <Box sx={{ height: 50, display: 'flex', alignItems: 'center' }}>
                        <ActionButton
                          bgcolor={channel.color}
                          hovercolor={channel.color.replace(/[0-9]$/, index === 0 ? '1' : index === 1 ? '1' : index === 2 ? '01' : 'a0')}
                          fullWidth
                          variant="contained"
                          sx={{
                            py: 1.5,
                            fontSize: '0.85rem',
                          }}
                          onClick={() => {
                            if (channel.contact.includes('@')) {
                              window.open(`mailto:${channel.contact}`);
                            } else if (channel.contact.includes('0850')) {
                              window.open(`tel:${channel.contact.replace(/\s/g, '')}`);
                            } else {
                              console.log(`${channel.title} desteği açılıyor...`);
                            }
                          }}
                        >
                          {channel.title.includes('Telefon') ? 'Hemen Ara' :
                            channel.title.includes('E-posta') ? 'E-posta Gönder' :
                              channel.title.includes('Sohbet') ? 'Sohbeti Başlat' :
                                'Randevu Al'}
                        </ActionButton>
                      </Box>
                    </CardContent>
                  </AnimatedCard>
                </Grow>
              </Grid>
            ))}
          </Grid>        </Container>
      </Box>

      {/* Support Categories Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          py: { xs: 6, md: 10 },
        }}
        id="categories"
      >
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: '#1976d2',
                  mb: 2,
                }}
              >
                Destek Kategorileri
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '600px',
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                }}
              >
                Hangi konuda yardıma ihtiyacınız var?
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4} sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            justifyContent: 'center',
            alignItems: 'stretch',
          }}>
            {supportCategories.map((category, index) => (
              <Grid item xs={12} md={6} key={index} sx={{
                width: { xs: '100%', md: '50%' },
              }}>
                <Slide
                  in
                  direction={index % 2 === 0 ? 'left' : 'right'}
                  timeout={1000 + index * 200}
                >
                  <AnimatedCard
                    delay={index * 0.3}
                    sx={{
                      p: 4,
                      background: 'white',
                      borderRadius: 4,
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                      height: '100%',
                      border: `2px solid ${category.color}15`,
                      '&:hover': {
                        boxShadow: `0 12px 40px ${category.color}25`,
                        transform: 'translateY(-8px)',
                        border: `2px solid ${category.color}30`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                        <Box
                          sx={{
                            width: 70,
                            height: 70,
                            borderRadius: 3,
                            background: `linear-gradient(135deg, ${category.color} 0%, ${category.color}dd 100%)`,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 3,
                            boxShadow: `0 6px 20px ${category.color}40`,
                          }}
                        >
                          {category.icon}
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: category.color,
                            fontSize: '1.4rem',
                          }}
                        >
                          {category.category}
                        </Typography>
                      </Box>

                      <Box >
                        {category.topics.map((topic, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              // py: 1.5,
                              borderRadius: 2,
                              // px: 2,
                              mb: 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: `${category.color}08`,
                                transform: 'translateX(8px)',
                                boxShadow: `2px 0 8px ${category.color}20`,
                              },
                            }}
                          >
                            <ChevronRight
                              sx={{
                                color: category.color,
                                fontSize: 20,
                                mr: 2,
                                minWidth: 'auto',
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.95rem',
                                color: 'text.primary',
                                lineHeight: 1.5,
                                fontWeight: 500,
                              }}
                            >
                              {topic}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </AnimatedCard>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }} id="contact-form">
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 700,
                color: '#1976d2',
                mb: 2,
              }}
            >
              Destek Talebi Oluştur
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              Detaylı sorularınız için form doldurarak bize ulaşın
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={6} justifyContent="center">
          <Grid item xs={12} md={8} display={'contents'}>
            <Paper
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: 4,
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                width: { xs: '100%', md: '50%' }
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Ad Soyad"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="E-posta"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Konu"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ width: '100%' }}>
                  <TextField
                    fullWidth
                    label="Mesaj"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    multiline
                    rows={6}
                    variant="outlined"
                    placeholder="Sorununuzu detayları ile açıklayın..."
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ width: { xs: '100%' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: '#1976d2',
                        '&:hover': { backgroundColor: '#1565c0' },
                        px: 6,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                      }}
                    >
                      Destek Talebi Gönder
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: { xs: 6, md: 10 } }} id="faq">
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  color: '#1976d2',
                  mb: 2,
                }}
              >
                Sıkça Sorulan Sorular
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  maxWidth: '600px',
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.2rem' },
                }}
              >
                Teknik destek hakkında merak ettikleriniz
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4}>
            <Grid item xs={12} md={8} sx={{ mx: 'auto' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {faqItems.map((item, index) => (
                  <Accordion
                    elevation={2}
                    sx={{
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': { margin: 0 },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        '&.Mui-expanded': { minHeight: 48 },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {item.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography color="text.secondary">
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>

                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            textAlign: 'center',
            p: 6,
            borderRadius: 3,
          }}
        >
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Hemen Destek Alın
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Sorununuz çözülene kadar yanınızdayız. 7/24 teknik destek ile
            güvenle çalışmaya devam edin.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open('tel:08502551906')}
              sx={{
                backgroundColor: '#42a5f5',
                '&:hover': { backgroundColor: '#1e88e5' },
                px: 2,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              Hemen Ara: 0850 255 19 06
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.open('mailto:teknikdestek@ronesis.com.tr')}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white',
                },
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
            >
              E-posta Gönder
            </Button>
          </Box>
        </Card>
      </Container>
    </Box>
  );
};

export default TeknikDestek;
